import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "~/atoms/button";
import { OverlaySpinner } from "../atoms/spinner";
import { changePassword } from "../api/v2/requests";
import { Input } from "~/atoms/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { useState } from "react";
import i18n from "~/i18n";
import { FetchError } from "~/api/types";

const PasswordFormSchema = z
  .object({
    current: z
      .string({ message: i18n.t("validations.required") })
      .min(1, i18n.t("validations.required")),
    new: z
      .string({ message: i18n.t("settings.password.tooShort") })
      .min(8, i18n.t("settings.password.tooShort"))
      .regex(/[A-Z]/, i18n.t("settings.password.needsUppercase"))
      .regex(/[a-z]/, i18n.t("settings.password.needsLowercase"))
      .regex(/[0-9]/, i18n.t("settings.password.needsNumber")),
    repeat: z
      .string({ message: i18n.t("validations.required") })
      .min(1, i18n.t("validations.required")),
  })
  .refine((data) => data.new === data.repeat, {
    message: i18n.t("settings.password.noMatch"),
    path: ["repeat"],
  });

type PasswordFormValues = z.infer<typeof PasswordFormSchema>;

export const AccountPassword = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(PasswordFormSchema),
    mode: "onChange",
  });

  const onSubmit = async (values: PasswordFormValues) => {
    try {
      setIsLoading(true);
      await changePassword(values.current, values.new);
      form.reset();
      toast.success(t("toasts.success.passUpdated"));
    } catch (error) {
      if (error instanceof FetchError && error.code === "INVALID_CREDENTIALS") {
        toast.error(t("toasts.error.incorectCurrentPass"));
        return;
      }
      toast.error(t("toasts.error.generic"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid w-[600px] gap-5">
      {isLoading && <OverlaySpinner />}

      <div>
        <p className="text-lg font-medium">{t("settings.password.title")}</p>
        <p className="text-muted-foreground text-sm">
          {t("settings.password.description")}
        </p>
      </div>
      <Form {...form}>
        <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="current"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>{t("settings.password.current")}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t("settings.password.enterCurrent")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="new"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>{t("settings.password.new")}</FormLabel>
                <FormDescription>
                  {t("settings.password.mustContain")}
                </FormDescription>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t("settings.password.enterNew")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="repeat"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>{t("settings.password.repeat")}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t("settings.password.repeat")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit">{t("commons.buttons.save")}</Button>
        </form>
      </Form>
    </div>
  );
};
